import React, { useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FolderMenu from "../FolderMenu";
import FolderIcon from "@mui/icons-material/Folder";
import { diamond, antiFlashWhite, darkGray } from "../../utils/StyleHelpers";
import { useFolderStore } from "../../stores/folderStore";
import DialogBox from "../DialogBox";
import { locale } from "../../utils/LocaleManager";
import { renameFolderRequest } from "../../models/folder";
import { renameFolder, deleteFolder } from "../../services/folderServices";
import TextFieldInput from "../Textfield";
import { Bounce, toast } from "react-toastify";
import { useContentStore } from "../../stores/contentStore";
import MoveFolder from "../MoveFolder";
import { handleError, isTextNeedsEllipsis } from "../../utils/utilFunctions";

const Folder = ({ setIsClickedFolder }: any) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [previousName, setPreviousName] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const {
    selectedId,
    setSelectedId,
    folderData,
    setFolderId,
    setIsRenameSuccess,
    isShowRename,
    setIsShowRename,
    isShowDelete,
    setIsShowDelete,
    setIsDeleteSuccess,
    isShowMoveFolder,
    setIsShowMoveFolder,
    setSelectedFolderId,
    setPreviousFolderName,
    setFolderName,
    setOpenFolderName,
  } = useFolderStore((state: any) => ({
    selectedId: state.selectedId,
    setSelectedId: state.setSelectedId,
    folderData: state.folderData,
    setFolderId: state.setFolderId,
    setIsRenameSuccess: state.setIsRenameSuccess,
    isShowRename: state.isShowRename,
    setIsShowRename: state.setIsShowRename,
    isShowDelete: state.isShowDelete,
    setIsShowDelete: state.setIsShowDelete,
    setIsDeleteSuccess: state.setIsDeleteSuccess,
    isShowMoveFolder: state.isShowMoveFolder,
    setIsShowMoveFolder: state.setIsShowMoveFolder,
    setSelectedFolderId: state.setSelectedFolderId,
    setPreviousFolderName: state.setPreviousFolderName,
    setFolderName: state.setFolderName,
    setOpenFolderName: state.setOpenFolderName,
  }));

  const { selectedFolders, setSelectedFolders, setSelectedThumbnails } =
    useContentStore((state: any) => ({
      selectedFolders: state.selectedFolders,
      setSelectedFolders: state.setSelectedFolders,
      setSelectedThumbnails: state.setSelectedThumbnails,
    }));

  const onMenuOpen = (e: React.MouseEvent<HTMLElement>, id: number) => {
    e.preventDefault();
    setSelectedId(id);
    setAnchorEl(e.currentTarget);
    setContextMenu(null);
    setOpen(true);
  };

  const pushSelectedFolder = (id: number, name: string) => {
    if (!selectedFolders.some((folder: any) => folder.id === id)) {
      setSelectedFolders([...selectedFolders, { id, name }]);
    }
  };

  const onContextMenuOpen = (
    e: React.MouseEvent<HTMLElement>,
    id: number,
    name: string,
  ) => {
    e.preventDefault();
    setSelectedId(id);
    setAnchorEl(null);
    setContextMenu({ mouseX: e.clientX + 2, mouseY: e.clientY - 6 });
    setOpen(true);
    pushSelectedFolder(id, name);
  };

  const fetchRenameFolder = async (
    id: string,
    request: renameFolderRequest,
  ) => {
    try {
      const renameAPI = await renameFolder(id, request);
      if (renameAPI?.status === 200) {
        setIsShowRename(false);
        setName("");
        setIsRenameSuccess(true);
        toast.success(`${previousName} renamed to ${name}`, {
          position: "bottom-left",
          autoClose: 2000,
          theme: "colored",
          transition: Bounce,
          onClose: () => setIsRenameSuccess(false),
        });
      } else {
        handleError({ status: renameAPI?.response?.status, toast });
      }
    } catch (error) {
      handleError({
        status: 0,
        toast,
        errorMessage: `Error renaming folder: ${error}`,
      });
    }
    setSelectedFolders([]);
  };

  const handleNewFolderName = () => {
    fetchRenameFolder(selectedId, { newName: name });
  };

  const deleteFolders = async () => {
    setIsShowDelete(false);
    toast.warning(`Processing: Deleting ${selectedFolders.length} folder/s`, {
      position: "bottom-left",
      autoClose: 1000,
      theme: "colored",
      transition: Bounce,
    });

    const deleted = await Promise.allSettled(
      selectedFolders.map((folder: any) => deleteFolder(folder.id)),
    );
    const deleteResult = deleted.filter(
      (result) => result.status === "rejected",
    );

    if (deleteResult.length === 0) {
      setIsDeleteSuccess(true);
      setSelectedFolders([]);
      toast.success("Folders Deleted", {
        position: "bottom-left",
        autoClose: 2000,
        theme: "colored",
        transition: Bounce,
      });
    } else {
      toast.error("Error: Encountered an error while deleting folder.", {
        position: "bottom-left",
        autoClose: 5000,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  const renderRenameDialog = () => (
    <TextFieldInput
      value={name}
      onChange={(e: any) => setName(e.target.value)}
      autoFocus
    />
  );

  const renderDeleteDialog = () => (
    <Box sx={{ width: "100%" }}>
      <Stack spacing={1}>
        {selectedFolders.map((folder: any) => (
          <Card variant="outlined" key={`folder-${folder.id}`}>
            <CardContent style={{ padding: "1.25rem" }}>
              <Stack direction="row" gap="0.625rem">
                <FolderIcon
                  sx={{
                    width: "1.25rem",
                    height: "1.25rem",
                    color: "darkGray",
                  }}
                />
                {folder.name}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );

  const handleRename = () => setIsShowRename(true);
  const handleDelete = () => setIsShowDelete(true);

  const selectFolder = (id: number, name: string) => {
    setSelectedFolders(selectedFolders.filter((item: any) => item.id !== id));
  };

  return (
    <Grid container spacing={{ xs: 2, md: 3, lg: 1 }}>
      {folderData.map((item: any) => (
        <Tooltip
          key={`tooltip-${item.id}`}
          title={isTextNeedsEllipsis(item?.text) ? item?.text : ""}
          placement="top-end"
          arrow
        >
          <Grid item xs={12} sm={12} md={4} lg={4} key={item.id}>
            <CardActionArea
              component="div"
              onDoubleClick={() => {
                setSelectedId(item.id);
                setIsClickedFolder(item);
                setPreviousName(item?.text);
                setFolderId(item.id);
                setSelectedThumbnails([]);
                setSelectedFolders([]);
                setFolderName(item?.text);
                setOpenFolderName(item?.text);
              }}
              onClick={() => {
                setPreviousName(item?.text);
                setName(item?.text);
                setSelectedFolderId(item?.id);
                setPreviousFolderName(item?.text);
                setFolderName(item?.text);
                selectFolder(item.id, item.text);
                pushSelectedFolder(item.id, item.text);
              }}
              style={{
                boxShadow: "unset",
              }}
            >
              <Card
                sx={{
                  maxHeight: 64,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderRadius: 1,
                  background: selectedFolders.find(
                    (folder: any) => folder?.id === item?.id,
                  )
                    ? diamond
                    : antiFlashWhite,
                  m: 1,
                  p: 1,
                  boxShadow: "unset",
                }}
                onContextMenu={(e) => onContextMenuOpen(e, item.id, item.text)}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FolderIcon style={{ color: "#444746" }} />
                    <Box
                      sx={{ ml: 1 }}
                      color={"#000"}
                      fontFamily={"Inter"}
                      fontSize={"20px"}
                      fontStyle={"normal"}
                      fontWeight={500}
                      lineHeight={"normal"}
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "15.38rem",
                      }}
                    >
                      {item.text}
                    </Box>
                  </Box>
                  <IconButton
                    aria-label="settings"
                    onClick={(e) => onMenuOpen(e, item.id)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </Card>
            </CardActionArea>
          </Grid>
        </Tooltip>
      ))}
      <FolderMenu
        open={open}
        setOpen={setOpen}
        anchorEl={anchorEl}
        setSelected={() => setSelectedId(null)}
        contextMenu={contextMenu}
        handleRename={handleRename}
        handleDelete={handleDelete}
        handleMove={() => setIsShowMoveFolder(true)}
      />

      {isShowRename ? (
        <DialogBox
          header={locale.control_rename}
          body={renderRenameDialog()}
          subBody={""}
          onCancel={() => setIsShowRename(false)}
          onSubmit={() => handleNewFolderName()}
          isDelete={false}
          primaryButton={locale.ok}
          secondaryButton={locale.cancel}
        />
      ) : (
        <></>
      )}

      {isShowDelete ? (
        <DialogBox
          header={locale.control_delete_folder_header}
          body={<div>{locale.delete_folder_description}</div>}
          subBody={renderDeleteDialog()}
          onCancel={() => setIsShowDelete(false)}
          onSubmit={() => deleteFolders()}
          isDelete={true}
          primaryButton={locale.yes}
          secondaryButton={locale.cancel}
        />
      ) : (
        <></>
      )}

      {isShowMoveFolder ? <MoveFolder /> : <></>}
    </Grid>
  );
};

export default Folder;
